<template>
  <div class="min-h-screen flex items-center justify-center bg-linear-to-br from-slate-50 to-slate-100 relative overflow-hidden">
    <!-- Decorative background elements -->
    <div class="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
      <div class="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-oxo-red/5 blur-3xl"></div>
      <div class="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-oxo-yellow/10 blur-3xl"></div>
    </div>

    <div class="z-10 w-full max-w-md p-6">
      <div class="bg-white/80 backdrop-blur-xl shadow-2xl rounded-2xl p-8 border border-white/50 transition-all duration-300 hover:shadow-xxxxl">
        
        <div class="text-center mb-8">
          <!-- Logo with animation -->
          <div class="relative inline-block group">
            <div class="absolute -inset-1 bg-linear-to-r from-oxo-red via-oxo-orange to-oxo-yellow rounded-2xl blur-sm opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
            <div class="relative rounded-2xl bg-white p-4">
               <img 
                v-if="logoUrl" 
                :src="logoUrl" 
                alt="OXO Logo" 
                class="h-16 w-auto mx-auto object-contain"
              />
              <span v-else class="text-4xl font-bold text-oxo-red">OXO</span>
            </div>
          </div>

          <h2 class="mt-6 text-2xl font-display font-bold text-slate-800 tracking-tight">
            Bienvenue sur <span class="text-transparent bg-clip-text bg-linear-to-r from-oxo-red to-oxo-orange">le Back Office</span>
          </h2>
          <p class="mt-2 text-sm text-slate-500">
            Connectez-vous pour accéder à votre espace d'administration
          </p>
        </div>

        <form class="space-y-5" @submit.prevent="handleLogin">
          
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-1.5 ml-1">Identifiant</label>
            <ClientOnly>
              <div class="relative group">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-oxo-red transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <input 
                  v-model="identifier" 
                  type="text" 
                  required
                  class="block w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl leading-5 bg-slate-50 placeholder-slate-400 focus:outline-hidden focus:bg-white focus:ring-2 focus:ring-oxo-red/20 focus:border-oxo-red transition-all duration-200 sm:text-sm"
                  placeholder="Votre email ou identifiant"
                />
              </div>
            </ClientOnly>
          </div>

          <div>
             <label class="block text-sm font-medium text-slate-700 mb-1.5 ml-1">Mot de passe</label>
            <ClientOnly>
              <div class="relative group">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-oxo-red transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input 
                  v-model="password" 
                  :type="showPassword ? 'text' : 'password'"
                  required
                  class="block w-full pl-10 pr-10 py-2.5 border border-slate-200 rounded-xl leading-5 bg-slate-50 placeholder-slate-400 focus:outline-hidden focus:bg-white focus:ring-2 focus:ring-oxo-red/20 focus:border-oxo-red transition-all duration-200 sm:text-sm"
                  placeholder="Votre mot de passe"
                />
                <button
                  type="button"
                  class="absolute right-2 bottom-1 text-slate-400 hover:text-slate-600 p-1"
                  @click="showPassword = !showPassword"
                >
                  <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              </div>
            </ClientOnly>
          </div>

          <div class="flex items-center justify-between pt-1">
            <label class="flex items-center cursor-pointer group">
              <div class="relative flex items-center">
                <input type="checkbox" v-model="rememberMe" class="peer h-4 w-4 text-oxo-red rounded-sm border-slate-300 focus:ring-oxo-red cursor-pointer transition-all">
              </div>
              <span class="ml-2 text-sm text-slate-600 group-hover:text-slate-800 transition-colors">Se souvenir de moi</span>
            </label>
            <NuxtLink to="/auth/forgot-password" class="text-sm font-medium text-oxo-red hover:text-oxo-orange transition-colors">Mot de passe oublié ?</NuxtLink>
          </div>

          <transition name="fade">
            <div v-if="error" class="p-4 rounded-xl bg-red-50 border border-red-100 flex items-start space-x-3">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-red-500 shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
              <span class="text-sm text-red-700 font-medium">{{ error }}</span>
            </div>
          </transition>

            <button 
            type="submit" 
            :disabled="loading" 
            class="w-full relative flex justify-center py-3 px-4 border border-transparent rounded-xl text-sm font-bold text-white bg-linear-to-r from-oxo-red to-oxo-orange hover:from-red-700 hover:to-orange-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-oxo-red disabled:opacity-70 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
          >
            <span v-if="loading" class="absolute left-0 inset-y-0 flex items-center pl-3">
              <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </span>
            <span>{{ loading ? 'Connexion en cours...' : 'Se connecter' }}</span>
          </button>

        </form>
        
        <div class="mt-8 text-center text-xs text-slate-400">
           &copy; {{ new Date().getFullYear() }} OXONET par l'École OXO. Tous droits réservés.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAuthService } from '~/composables/useAuthService'

definePageMeta({
  layout: false
})

const router = useRouter()
const { login } = useAuthService()

const identifier = ref('')
const password = ref('')
const showPassword = ref(false)
const rememberMe = ref(false)
const loading = ref(false)
const error = ref('')
const logoUrl = ref('')

// Fetch logo from API (via proxy)
onMounted(async () => {
  try {
    const data = await $fetch<any>('/api/oxoweb/school/branding')
    logoUrl.value = data?.logoLight || data?.logoDark || ''
  } catch (e) {
    console.error('Failed to fetch logo:', e)
  }
})

const handleLogin = async () => {
    loading.value = true
    error.value = ''
    try {
        await login(identifier.value, password.value, rememberMe.value)
        router.push('/')
    } catch (e: any) {
        console.error(e)
        
        // Handle rate limiting (429)
        if (e.statusCode === 429 || e.status === 429) {
          error.value = 'Trop de tentatives de connexion. Veuillez patienter 15 minutes avant de réessayer.'
        } else if (e.statusCode === 401 || e.status === 401) {
          error.value = 'Identifiants incorrects. Veuillez vérifier votre email et mot de passe.'
        } else {
          error.value = e.data?.message || e.message || 'Une erreur est survenue. Veuillez réessayer.'
        }
    } finally {
        loading.value = false
    }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>

